import {
  Button,
  chakra,
  Flex,
  Modal, ModalBody, ModalCloseButton,
  ModalContent, ModalFooter, ModalHeader,
  ModalOverlay,
  useToast
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { showErrorToast } from "../../../../utils";
import { SUB_LEVER_PARAMETERS } from "../../helpers";
import { Lever } from "./components";
import { useEffect } from "react";

function mapServerDataToFormData(data) {
  const formData = {};

  data.forEach(lever => {
    formData[`${lever.id}totalCo2Emissions`] = lever.totalCo2Emissions;
    formData[`${lever.id}selected`] = lever.selected;
    lever.subLevers.forEach(subLever => {
      Object.values(SUB_LEVER_PARAMETERS).forEach(param => {
        formData[`${lever.id}${subLever.id}${param}`] = subLever[param];
      });
    });
  });

  return formData;
}

function mapFormDataToApiData(data, formData) {
  return {
    levers: data.map(leverElement => {
      return {
        ...leverElement,
        totalCo2Emissions: formData[`${leverElement.id}totalCo2Emissions`],
        selected: formData[`${leverElement.id}selected`],
        subLevers: leverElement.subLevers.map(subLeverElement => {
          const subLeverParams = {};
          Object.values(SUB_LEVER_PARAMETERS).filter(param => param !== SUB_LEVER_PARAMETERS.totalCo2Saved).forEach(param => {
            subLeverParams[param] = formData[`${leverElement.id}${subLeverElement.id}${param}`];
          });
          return {
            ...subLeverElement,
            ...subLeverParams
          };
        })
      };
    })
  };
}

function ParametersModal({ modalData, baseYear, isOpen, onClose, onSubmit, onLoadLeverClick }) {
  const { t } = useTranslation("translation");
  const toast = useToast();

  const {
    register,
    formState: { errors },
    handleSubmit,
    control,
    watch,
    setValue,
    reset,
    clearErrors
  } = useForm({
    defaultValues: mapServerDataToFormData(modalData)
  });

  useEffect(() => {
    if (modalData) {
      reset(mapServerDataToFormData(modalData));
    }
  }, [modalData, reset]);

  const prepareSubmitData = (formData, event, preventCloseModal) => {
    const dataToSave = mapFormDataToApiData(modalData, formData);
    const exceededSumLevers = [];

    dataToSave.levers.forEach(lever => {
      let emissionsSavedSum = 0;
      lever.subLevers.forEach(subLever => {
        if (subLever[SUB_LEVER_PARAMETERS.emissionsSaved] !== "") {
          emissionsSavedSum += Number(subLever[SUB_LEVER_PARAMETERS.emissionsSaved]);
        }
      });
      if (emissionsSavedSum > 100) {
        exceededSumLevers.push(`'${lever.name}'`);
      }
    });

    if (exceededSumLevers.length > 0) {
      showErrorToast(toast,
        t("decarbonization.leversParametersModal.errorHandling.uptakesErrorMsg") +
        `${exceededSumLevers.length > 1 ?
          `${t("decarbonization.leversParametersModal.errorHandling.multipleExceeded", { levers: exceededSumLevers.join(", ") })}` :
          `${t("decarbonization.leversParametersModal.errorHandling.singleExceeded", { lever: exceededSumLevers[0] })}`
        }`
      );
    } else {
      onSubmit(dataToSave, preventCloseModal);
    }
  };

  return !isOpen ? null : <Modal isOpen={isOpen} onClose={onClose} size="6xl">
    <ModalOverlay />
    <ModalContent>
      <chakra.form onSubmit={handleSubmit(prepareSubmitData)}>
        <ModalHeader>
          {t("decarbonization.leversParametersModal.header")}
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody maxHeight={"65vh"} overflow="auto">
          {modalData.map((lever, index) => {
            return <Lever
              key={lever.name}
              leverData={lever}
              leverIndex={index}
              baseYear={baseYear}
              register={register}
              errors={errors}
              control={control}
              watch={watch}
              setValue={setValue}
              clearErrors={clearErrors}
            />;
          })}
        </ModalBody>
        <ModalFooter justifyContent="space-between">
          <Button colorScheme="gray" onClick={onClose}>
            {t("buttons.close")}
          </Button>
          <Flex gap={3}>
            <Button
              colorScheme="gray"
              onClick={onLoadLeverClick}
            >
              {t("decarbonization.leversParametersModal.buttons.loadLevers")}
            </Button>
            <Button
              colorScheme="gray"
              onClick={handleSubmit((data, event) => prepareSubmitData(data, event, true))}
            >
              {t("decarbonization.leversParametersModal.buttons.calculate")}
            </Button>
            <Button colorScheme="customScheme" type="submit">
              {t("decarbonization.leversParametersModal.buttons.calculateAndClose")}
            </Button>
          </Flex>
        </ModalFooter>
      </chakra.form>
    </ModalContent>
  </Modal>;
}

export default ParametersModal;